package org.zq.astrock_ai.network

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.api.StockCodes
import org.zq.astrock_ai.api.dto.KlineBarDto
import org.zq.astrock_ai.api.dto.KlineResponseDto
import org.zq.astrock_ai.api.dto.StockQuoteDto
import org.zq.astrock_ai.util.formatDecimal

/**
 * 客户端直连东财行情（当后端 /kline 或 /watchlist 不可用时兜底）。
 *
 * - 报价：stock/get + fltt=2
 * - 分时：push2 trends（当日实时）
 * - 五日：push2his trends ndays=5（跨交易日）
 * - 日/周/月：历史 K 线
 */
class EastMoneyDirectClient(
    private val client: HttpClient = createMarketHttpClient(),
) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun fetchQuotes(codes: List<String>): List<StockQuoteDto> = coroutineScope {
        codes.distinct().map { code ->
            async { runCatching { fetchQuote(code) }.getOrNull() }
        }.awaitAll().filterNotNull()
    }

    suspend fun fetchQuote(code: String): StockQuoteDto? {
        val normalized = code.filter { it.isDigit() }.takeLast(6)
        if (normalized.length != 6) return null
        val text = client.get(STOCK_URL) {
            parameter("secid", StockCodes.toSecId(normalized))
            parameter("fltt", 2)
            parameter("fields", "f43,f44,f45,f46,f47,f48,f57,f58,f169,f170")
            parameter("ut", MARKET_TOKEN)
            header("User-Agent", UA)
        }.bodyAsText()
        val data = json.parseToJsonElement(text).jsonObject["data"]?.jsonObject ?: return null
        fun num(key: String): Double? = data[key]?.jsonPrimitive?.doubleOrNull
        fun text(key: String): String =
            data[key]?.jsonPrimitive?.contentOrNull.orEmpty().takeUnless { it == "-" }.orEmpty()
        val price = num("f43")?.takeIf { it > 0 } ?: return null
        val changePct = num("f170") ?: 0.0
        val changeAmt = num("f169") ?: 0.0
        val open = num("f46") ?: price
        val high = num("f44") ?: price
        val low = num("f45") ?: price
        val volume = num("f47") ?: 0.0
        val turnover = num("f48") ?: 0.0
        val name = text("f58")
        val resolved = text("f57").ifBlank { normalized }
        return StockQuoteDto(
            name = name.ifBlank { resolved },
            code = resolved,
            fullCode = StockCodes.toFullCode(resolved),
            price = "¥${formatDecimal(price)}",
            changePercent = "${if (changePct >= 0) "+" else ""}${formatDecimal(changePct)}%",
            changeAmount = "${if (changeAmt >= 0) "+" else ""}${formatDecimal(changeAmt)}",
            isUp = changePct >= 0,
            openPrice = formatDecimal(open),
            highPrice = formatDecimal(high),
            lowPrice = formatDecimal(low),
            turnover = formatTurnover(turnover),
            volume = formatVolume(volume),
        )
    }

    suspend fun fetchKline(
        code: String,
        period: ChartPeriod = ChartPeriod.DAY,
        limit: Int = period.defaultLimit,
    ): KlineResponseDto {
        val bars = when (period) {
            ChartPeriod.INTRADAY -> {
                val live = fetchTrends(code, ndays = 1, historical = false)
                when {
                    live.isNotEmpty() -> live
                    else -> {
                        val his = fetchTrends(code, ndays = 1, historical = true)
                        if (his.isNotEmpty()) his
                        else fetchHistoryKline(code, limit = 48, klt = 5).withRunningAvg()
                    }
                }
            }
            ChartPeriod.FIVE_DAY -> {
                // 五日必须走 push2his，push2 的 ndays=5 往往只有当日
                val his = fetchTrends(code, ndays = 5, historical = true)
                if (his.isNotEmpty()) his
                else fetchHistoryKline(code, limit = 48 * 5, klt = 5).withRunningAvg()
            }
            ChartPeriod.WEEK -> fetchHistoryKline(code, limit, klt = 102)
            ChartPeriod.MONTH -> fetchHistoryKline(code, limit, klt = 103)
            ChartPeriod.DAY -> fetchHistoryKline(code, limit, klt = 101)
        }
        return KlineResponseDto(
            code = code,
            bars = bars,
            period = period.apiValue,
            chartType = period.chartType,
        )
    }

    suspend fun fetchBenchmarkKline(
        secid: String,
        limit: Int = 120,
    ): KlineResponseDto = KlineResponseDto(
        code = secid,
        bars = fetchHistoryKlineBySecId(secid, limit, klt = 101),
        period = ChartPeriod.DAY.apiValue,
        chartType = ChartPeriod.DAY.chartType,
    )

    private suspend fun fetchHistoryKline(code: String, limit: Int, klt: Int): List<KlineBarDto> {
        return fetchHistoryKlineBySecId(StockCodes.toSecId(code), limit, klt)
    }

    private suspend fun fetchHistoryKlineBySecId(
        secid: String,
        limit: Int,
        klt: Int,
    ): List<KlineBarDto> {
        // 用 lmt 限制条数；beg=0 会返回全部历史（数千根），容易请求超时。
        val url =
            "https://push2his.eastmoney.com/api/qt/stock/kline/get" +
                "?fields1=f1,f2,f3,f4,f5,f6" +
                "&fields2=f51,f52,f53,f54,f55,f56,f57,f58,f59,f60,f61" +
                "&end=20500101&lmt=${limit.coerceIn(1, 1000)}" +
                "&ut=fa5fd1943c7b386f172d6893dbfba10b&rtntype=6" +
                "&secid=$secid&klt=$klt&fqt=1"
        val text = client.get(url) { header("User-Agent", UA) }.bodyAsText()
        val body = json.decodeFromString<EmEnvelope<EmKlineBody>>(text)
        return body.data?.klines.orEmpty().mapNotNull { parseKlineLine(it) }.takeLast(limit)
    }

    /**
     * @param historical true 使用 push2his（支持多日）；false 使用 push2（当日实时）
     */
    private suspend fun fetchTrends(
        code: String,
        ndays: Int,
        historical: Boolean,
    ): List<KlineBarDto> {
        val secid = StockCodes.toSecId(code)
        val host = if (historical) "push2his.eastmoney.com" else "push2.eastmoney.com"
        val url =
            "https://$host/api/qt/stock/trends2/get?secid=$secid" +
                "&fields1=f1,f2,f3,f4,f5,f6,f7,f8,f9,f10,f11,f12,f13" +
                "&fields2=f51,f52,f53,f54,f55,f56,f57,f58" +
                "&iscr=0&ndays=$ndays&iscca=0&ut=fa5fd1943c7b386f172d6893dbfba10b"
        val text = client.get(url) { header("User-Agent", UA) }.bodyAsText()
        val body = json.decodeFromString<EmEnvelope<EmTrendsBody>>(text)
        val pre = body.data?.prePrice?.toFloat()
        val raw = body.data?.trends.orEmpty().mapNotNull { parseTrendLine(it) }
        if (raw.isEmpty()) return emptyList()
        var prev = pre?.takeIf { it > 0f } ?: raw.first().close
        return raw.map { cur ->
            val open = if (cur.open > 0f) cur.open else prev
            val bar = cur.copy(
                open = open,
                high = maxOf(open, cur.close, cur.high, cur.avg ?: cur.close),
                low = minOf(open, cur.close, cur.low.takeIf { it > 0f } ?: cur.close, cur.avg ?: cur.close),
            )
            prev = cur.close
            bar
        }
    }

    /**
     * 兼容两种 trends 行格式：
     * - 短：时间,现价,均价,成交量
     * - 长：时间,开,收,高,低,量,额,均价
     */
    private fun parseTrendLine(line: String): KlineBarDto? {
        val p = line.split(",")
        if (p.size < 4) return null
        return if (p.size >= 7) {
            var open = p[1].toFloatOrNull() ?: return null
            val close = p[2].toFloatOrNull() ?: return null
            val high = p[3].toFloatOrNull() ?: close
            val low = p[4].toFloatOrNull() ?: close
            val volume = p[5].toFloatOrNull() ?: 0f
            val avg = p.getOrNull(7)?.toFloatOrNull()
                ?: p.lastOrNull()?.toFloatOrNull()?.takeIf { it in 0.01f..10_000f && it < volume }
            if (open <= 0f) open = close
            KlineBarDto(
                date = p[0],
                open = open,
                close = close,
                high = maxOf(high, close, open),
                low = minOf(if (low > 0f) low else close, close, open),
                volume = volume,
                avg = avg,
            )
        } else {
            val price = p[1].toFloatOrNull() ?: return null
            val avg = p[2].toFloatOrNull()
            val volume = p[3].toFloatOrNull() ?: 0f
            KlineBarDto(
                date = p[0],
                open = price,
                close = price,
                high = maxOf(price, avg ?: price),
                low = minOf(price, avg ?: price),
                volume = volume,
                avg = avg,
            )
        }
    }

    private fun parseKlineLine(line: String): KlineBarDto? {
        val p = line.split(",")
        if (p.size < 6) return null
        val open = p[1].toFloatOrNull() ?: return null
        val close = p[2].toFloatOrNull() ?: return null
        val high = p[3].toFloatOrNull() ?: return null
        val low = p[4].toFloatOrNull() ?: return null
        val volume = p[5].toFloatOrNull() ?: 0f
        return KlineBarDto(
            date = p[0],
            open = open,
            close = close,
            high = high,
            low = low,
            volume = volume,
        )
    }

    private fun List<KlineBarDto>.withRunningAvg(): List<KlineBarDto> {
        if (isEmpty()) return this
        var sum = 0.0
        return mapIndexed { index, bar ->
            sum += bar.close
            bar.copy(avg = (sum / (index + 1)).toFloat())
        }
    }

    @Serializable
    private data class EmEnvelope<T>(@SerialName("data") val data: T? = null)

    @Serializable
    private data class EmKlineBody(@SerialName("klines") val klines: List<String>? = null)

    @Serializable
    private data class EmTrendsBody(
        @SerialName("prePrice") val prePrice: Double? = null,
        @SerialName("trends") val trends: List<String>? = null,
    )

    private fun formatTurnover(v: Double): String = when {
        v >= 100_000_000 -> "${formatDecimal(v / 100_000_000)}亿"
        v >= 10_000 -> "${formatDecimal(v / 10_000)}万"
        else -> formatDecimal(v)
    }

    private fun formatVolume(v: Double): String =
        if (v >= 10_000) "${formatDecimal(v / 10_000)}万手" else "${formatDecimal(v, 0)}手"

    companion object {
        private const val UA = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
        private const val STOCK_URL = "https://push2.eastmoney.com/api/qt/stock/get"
        private const val MARKET_TOKEN = "fa5fd1943c7b386f172d6893dbfba10b"
    }
}
